import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Rating,
  CircularProgress,
  Alert,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Edit,
  PhotoCamera,
  Save,
  Cancel,
  LocationOn,
  Phone,
  Email,
  CalendarToday,
  Star,
  Verified,
  Work,
  School,
  Language,
  Person,
  Settings,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useParams } from 'react-router-dom';

const Profile = () => {
  const { id } = useParams();
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [imageDialog, setImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedBookings: 0,
    averageRating: 0,
    totalReviews: 0,
  });

  const isOwnProfile = !id || id === user?.id?.toString();

  useEffect(() => {
    fetchProfile();
    if (isOwnProfile) {
      fetchUserStats();
    }
  }, [id]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const targetId = id || user?.id;
      const response = await axios.get(`/api/users/profile/${targetId}`);
      
      if (response.data.success) {
        setProfileData(response.data.data);
        setEditData(response.data.data);
        if (response.data.data.reviews) {
          setReviews(response.data.data.reviews);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showError('Error al cargar el perfil');
      // Use mock data for development
      const mockProfile = {
        id: user?.id || 1,
        name: user?.name || 'Usuario Demo',
        email: user?.email || 'demo@example.com',
        phone: '+52 55 1234 5678',
        location: 'Bogotá, Colombia',
        bio: 'Usuario activo de la plataforma Jooru. Me gusta mantener mi hogar limpio y ordenado.',
        image: '/api/placeholder/200/200',
        joinDate: '2023-01-15',
        verified: true,
        languages: ['Español', 'Inglés'],
        interests: ['Limpieza', 'Organización', 'Decoración'],
        socialMedia: {
          facebook: '',
          instagram: '',
          twitter: '',
        },
      };
      setProfileData(mockProfile);
      setEditData(mockProfile);
      setReviews(mockReviews);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get('/api/users/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Mock stats for development
      setStats({
        totalBookings: 12,
        completedBookings: 10,
        averageRating: 4.8,
        totalReviews: 8,
      });
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await axios.put('/api/users/profile', editData);
      
      if (response.data.success) {
        setProfileData(response.data.data);
        if (isOwnProfile) {
          updateUser(response.data.data);
        }
        setEditing(false);
        showSuccess('Perfil actualizado exitosamente');
      }
    } catch (error) {
      showError('Error al actualizar el perfil');
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;
    
    const formData = new FormData();
    formData.append('image', selectedImage);
    
    try {
      const response = await axios.post('/api/users/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        const updatedProfile = { ...profileData, image: response.data.imageUrl };
        setProfileData(updatedProfile);
        if (isOwnProfile) {
          updateUser(updatedProfile);
        }
        setImageDialog(false);
        setSelectedImage(null);
        showSuccess('Imagen actualizada exitosamente');
      }
    } catch (error) {
      showError('Error al subir la imagen');
    }
  };

  const handleCancelEdit = () => {
    setEditData(profileData);
    setEditing(false);
  };

  // Mock reviews for development
  const mockReviews = [
    {
      id: 1,
      professional: {
        name: 'María González',
        image: '/api/placeholder/50/50',
      },
      service: 'Limpieza General del Hogar',
      rating: 5,
      comment: 'Excelente cliente, muy amable y respetuoso.',
      date: '2024-01-15',
    },
    {
      id: 2,
      professional: {
        name: 'Carlos Rodríguez',
        image: '/api/placeholder/50/50',
      },
      service: 'Reparación de Tuberías',
      rating: 5,
      comment: 'Cliente muy organizado, facilitó mucho el trabajo.',
      date: '2024-01-10',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Perfil no encontrado
        </Typography>
        <Button variant="contained" onClick={() => window.history.back()}>
          Volver
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    isOwnProfile && (
                      <IconButton
                        size="small"
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                        }}
                        onClick={() => setImageDialog(true)}
                      >
                        <PhotoCamera fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <Avatar
                    src={profileData.image}
                    alt={profileData.name}
                    sx={{
                      width: 150,
                      height: 150,
                      border: 4,
                      borderColor: 'primary.main',
                    }}
                  >
                    {profileData.name?.charAt(0)}
                  </Avatar>
                </Badge>
                
                {profileData.verified && (
                  <Verified
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      fontSize: 32,
                      backgroundColor: 'white',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {editing ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={editData.name || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Biografía"
                    multiline
                    rows={3}
                    value={editData.bio || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Cuéntanos un poco sobre ti..."
                  />
                </Box>
              ) : (
                <Box>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {profileData.name}
                  </Typography>
                  
                  {isOwnProfile && stats.totalReviews > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating value={stats.averageRating} readOnly precision={0.1} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {stats.averageRating}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        ({stats.totalReviews} reseñas)
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn color="action" />
                    <Typography variant="body1">{profileData.location}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarToday color="action" />
                    <Typography variant="body1">
                      Miembro desde {new Date(profileData.joinDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  {profileData.bio && (
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                      {profileData.bio}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={3}>
              {isOwnProfile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {editing ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSaveProfile}
                        fullWidth
                      >
                        Guardar
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        fullWidth
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => setEditing(true)}
                      fullWidth
                    >
                      Editar Perfil
                    </Button>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      <Grid container spacing={4}>
        {/* Stats Cards (only for own profile) */}
        {isOwnProfile && (
          <Grid item xs={12}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <CalendarToday color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.totalBookings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reservas Totales
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Work color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.completedBookings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completadas
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Star color="warning" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.averageRating}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Calificación Promedio
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Person color="info" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.totalReviews}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reseñas Recibidas
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Información Personal
            </Typography>
            
            {editing ? (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={editData.phone || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ubicación"
                    value={editData.location || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, location: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Idiomas (separados por coma)"
                    value={editData.languages?.join(', ') || ''}
                    onChange={(e) => setEditData(prev => ({ 
                      ...prev, 
                      languages: e.target.value.split(',').map(lang => lang.trim()).filter(lang => lang)
                    }))}
                    placeholder="Español, Inglés, Francés"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Intereses (separados por coma)"
                    value={editData.interests?.join(', ') || ''}
                    onChange={(e) => setEditData(prev => ({ 
                      ...prev, 
                      interests: e.target.value.split(',').map(interest => interest.trim()).filter(interest => interest)
                    }))}
                    placeholder="Limpieza, Organización, Decoración"
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Email color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {profileData.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Phone color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Teléfono
                      </Typography>
                      <Typography variant="body1">
                        {profileData.phone || 'No especificado'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                {profileData.languages && profileData.languages.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Idiomas
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {profileData.languages.map((language) => (
                        <Chip
                          key={language}
                          label={language}
                          variant="outlined"
                          icon={<Language />}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                
                {profileData.interests && profileData.interests.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Intereses
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {profileData.interests.map((interest) => (
                        <Chip
                          key={interest}
                          label={interest}
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>

          {/* Reviews Section */}
          {reviews.length > 0 && (
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Reseñas Recibidas
              </Typography>
              
              <List>
                {reviews.map((review, index) => (
                  <React.Fragment key={review.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar src={review.professional.image} alt={review.professional.name} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {review.professional.name}
                            </Typography>
                            <Rating value={review.rating} readOnly size="small" />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(review.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                              {review.service}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {review.comment}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < reviews.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions */}
          {isOwnProfile && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Acciones Rápidas
              </Typography>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Settings />}
                sx={{ mb: 2 }}
                onClick={() => window.location.href = '/dashboard'}
              >
                Ir al Dashboard
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CalendarToday />}
                onClick={() => window.location.href = '/dashboard?tab=0'}
              >
                Ver Mis Reservas
              </Button>
            </Paper>
          )}

          {/* Activity Summary */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Resumen de Actividad
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Miembro desde
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {new Date(profileData.joinDate).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
            
            {isOwnProfile && (
              <>
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Servicios utilizados
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {stats.completedBookings} servicios
                  </Typography>
                </Box>
                
                {stats.totalReviews > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Calificación como cliente
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={stats.averageRating} readOnly size="small" />
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {stats.averageRating} ({stats.totalReviews})
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar Foto de Perfil</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={(e) => setSelectedImage(e.target.files[0])}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCamera />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Seleccionar Imagen
              </Button>
            </label>
            
            {selectedImage && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Imagen seleccionada: {selectedImage.name}
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                  La imagen debe ser menor a 5MB y en formato JPG, PNG o GIF.
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleImageUpload}
            disabled={!selectedImage}
          >
            Subir Imagen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;